import { fetchWithPolicy } from './requestPolicy';

// Every request in this file goes through fetchWithPolicy, which supplies the
// timeout this client never had (a request that never settles left a permanent
// spinner on every QM Admin page) and the retry-once-on-network-failure rule
// this client also never had. Same module as api.ts: one policy, not two.
//
// The rule to keep in mind when adding a call here: a MUTATION that times out is
// UNKNOWN, not failed. fetchWithPolicy never retries one and never tells the
// user it failed, because the server may already have acted.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://web-production-9f6e9.up.railway.app';

interface ApiResponse<T> {
  data?: T;
  error?: string;
  // item 1c: dual-role prevention gates (assign_distributor, assign_admin)
  // return 409 { error_code: "role_conflict_requires_confirm", error: <generic
  // message> } when a confirm is required. Threaded through additively so
  // callers can branch on the machine code without parsing `error` text.
  error_code?: string;
  status?: number;
}

// C-02: admin endpoints require admin scope. While impersonating, quoteme_token is the
// impersonated (non-admin) JWT and quoteme_admin_token holds the real admin JWT (set on
// impersonate, removed on un-impersonate). Prefer the admin token so admin writes
// (e.g. "Save States" on the distributor detail) authorize instead of 403-ing.
export function selectAdminToken(adminToken: string | null, userToken: string | null): string | null {
  return adminToken || userToken;
}

function getAuthToken(): string | null {
  return selectAdminToken(
    localStorage.getItem('quoteme_admin_token'),
    localStorage.getItem('quoteme_token'),
  );
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetchWithPolicy(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        error: errorData.error || `Request failed (${response.status})`,
        error_code: errorData.error_code,
        status: response.status,
      };
    }

    if (response.status === 204) return { data: undefined as T };

    const data = await response.json();
    return { data };
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Network error' };
  }
}

// ============= TYPES =============

export interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  role: string;
  status: string;
  // Feature 2 Slice 1: honest, derived-from-existing-columns status. One of
  // "invite_sent" | "active" | "archived". See User#display_status (BE) and
  // userStatusPill (utils/userDisplayStatus.ts) for the full derivation.
  // Optional so older cached payloads / tests without it still typecheck.
  display_status?: string;
  distributor_name: string | null;
  claimed_distributor_id: string | null;
  flagged_for_review: boolean;
  flag_reason: string | null;
  last_login_at: string | null;
  created_at: string;
  rep_profile: {
    id: string;
    distributor_id: string | null;
    phone: string | null;
    territory: string | null;
    is_active: boolean;
  } | null;
}

export interface AdminStats {
  pending_quotes: number;
  flagged_users: number;
  new_signups_week: number;
  unassociated_reps: number;
}

export interface AdminDistributor {
  id: string;
  name: string;
  email_domain: string | null;
  region: string | null;
  status: string;
  logo_url: string | null;
  created_at: string;
  rep_count: number;
  product_count: number;
  admin_user_id: string | null;
  admin_user_name: string | null;
  unclaimed: boolean;
  /** Percentage of products with a non-"other" category (0–100), null if no catalog. */
  categorization_pct: number | null;
  /** USPS two-letter state codes this distributor services. */
  service_states: string[];
  /** Primary state/province code this distributor is headquartered in or primarily serves. */
  primary_state: string | null;
  /** ISO country code ("US" | "CA"); defaults to "US" server-side. */
  country?: string | null;
}

export interface AdminDistributorDetail extends AdminDistributor {
  order_days: string | null;
  minimum_order_cents: number;
  // P0-2b: qm-admin quota lever fields (serialize_detail). effective_quota is
  // the base free-quote allowance (5) plus whatever bonus_free_quotes has
  // been granted via grantFreeQuotes. See QuotaResetControl in
  // pages/admin/QMAdminDistributorDetail.tsx.
  bonus_free_quotes?: number;
  effective_quota?: number;
  branding_slug?: string | null;
  admins: Array<{
    user_id: string;
    name: string;
    email: string;
    role: string;
    status: string;
  }>;
  reps: Array<{
    id: string;
    user_id: string;
    name: string;
    email: string;
    phone: string | null;
    territory: string | null;
    is_active: boolean;
  }>;
  catalog: {
    id: string;
    product_count: number;
    uploaded_at: string;
  } | null;
  catalogs?: Array<{
    id: string;
    uploaded_at: string;
    original_filename: string | null;
    uploaded_by: string | null;
    product_count: number | null;
    pct_uncategorized: number | null;
    status: string;
  }>;
  restaurants: Array<{
    id: string;
    name: string;
    city: string | null;
    state: string | null;
  }>;
}

export interface AdminRestaurant {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  status: string;
  contact_count: number;
  restaurant_group: { id: string; name: string } | null;
  created_at: string;
  admin_user_id: string | null;
  admin_user_name: string | null;
  restaurant_admin_id: string | null;
  restaurant_admin_name: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  zip: string | null;
  website: string | null;
  google_place_id: string | null;
  // Not yet populated by the crawler; may be null.
  source_state: string | null;
  // e.g. "place_id_conflict"; may be null.
  data_flags: string | null;
  // Menu kinds present among CURRENT menu sources, e.g. ["wine","dinner"].
  menu_coverage: string[];
}

export interface ConferenceLead {
  id: string;
  conference_name: string | null;
  company_name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_title: string | null;
  lead_type: string;
  voice_note_transcript: string | null;
  notes: string | null;
  status: string;
  converted_to_type: string | null;
  converted_to_id: string | null;
  booth_photo_url: string | null;
  card_photo_url: string | null;
  email_sent_at: string | null;
  email_sent_by_user_id: string | null;
  email_send_count: number;
  created_at: string;
  captured_by: { id: string; name: string } | null;
}

export interface AdminBrand {
  id: string;
  name: string;
  status: string;
  logo_url?: string | null;
  website?: string | null;
  created_at: string;
}

export interface AdminBrandDetail {
  id: string;
  name: string;
  status: string;
  website: string | null;
  logo_url: string | null;
  category: string | null;
  created_at: string;
  stats: {
    products: number;
    packages: number;
    distributors: number;
  };
  users: Array<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    status: string;
    last_login_at: string | null;
  }>;
  catalogs: Array<{
    id: string;
    original_filename: string | null;
    row_count: number | null;
    uploaded_at: string;
  }>;
  packages: Array<{
    id: string;
    title: string;
    status: string;
    item_count: number;
    sent_at: string | null;
  }>;
  distributors: Array<{
    distributor_id: string;
    name: string;
    status: string;
    connected_since: string;
  }>;
}

// ============= ADMIN STATS =============

export async function getAdminStats(): Promise<ApiResponse<AdminStats>> {
  return fetchWithAuth('/api/v1/admin/users/stats');
}

// ============= ADMIN USERS =============

export async function getAdminUsers(params?: {
  role?: string;
  flagged?: boolean;
  status?: string;
  since?: string;
  include_archived?: boolean;
}): Promise<ApiResponse<AdminUser[]>> {
  const searchParams = new URLSearchParams();
  if (params?.role) searchParams.set('role', params.role);
  if (params?.flagged) searchParams.set('flagged', 'true');
  if (params?.status) searchParams.set('status', params.status);
  if (params?.since) searchParams.set('since', params.since);
  if (params?.include_archived) searchParams.set('include_archived', 'true');
  const qs = searchParams.toString();
  return fetchWithAuth(`/api/v1/admin/users${qs ? `?${qs}` : ''}`);
}

export async function updateAdminUser(
  id: string,
  data: Partial<Pick<AdminUser, 'status' | 'role' | 'first_name' | 'last_name' | 'phone'>>
): Promise<ApiResponse<AdminUser>> {
  return fetchWithAuth(`/api/v1/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function inviteAdminUser(data: {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  distributor_id?: string;
}): Promise<ApiResponse<AdminUser>> {
  return fetchWithAuth('/api/v1/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function resendInvite(userId: string): Promise<ApiResponse<{ message: string }>> {
  return fetchWithAuth(`/api/v1/admin/users/${userId}/resend_invite`, {
    method: 'POST',
  });
}

export async function resendWelcome(userId: string): Promise<ApiResponse<{ ok: boolean; sent_to: string }>> {
  return fetchWithAuth(`/api/v1/admin/users/${userId}/resend_welcome`, {
    method: 'POST',
  });
}

export async function assignDistributor(
  userId: string,
  distributorId: string,
  confirm?: boolean
): Promise<ApiResponse<AdminUser>> {
  return fetchWithAuth(`/api/v1/admin/users/${userId}/assign_distributor`, {
    method: 'PATCH',
    body: JSON.stringify({ distributor_id: distributorId, ...(confirm ? { confirm: true } : {}) }),
  });
}

// ============= ADMIN DISTRIBUTORS =============

export async function getAdminDistributors(): Promise<ApiResponse<AdminDistributor[]>> {
  return fetchWithAuth('/api/v1/admin/distributors');
}

export async function getAdminDistributor(id: string): Promise<ApiResponse<AdminDistributorDetail>> {
  return fetchWithAuth(`/api/v1/admin/distributors/${id}`);
}

export async function createDistributor(data: {
  name: string;
  email_domain?: string;
  region?: string;
  admin_email?: string;
  admin_first_name?: string;
  admin_last_name?: string;
}): Promise<ApiResponse<AdminDistributorDetail>> {
  return fetchWithAuth('/api/v1/admin/distributors', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminDistributor(
  id: string,
  data: {
    name?: string;
    email_domain?: string;
    region?: string;
    primary_state?: string | null;
    service_states?: string[];
    country?: string;
  }
): Promise<ApiResponse<AdminDistributorDetail>> {
  return fetchWithAuth(`/api/v1/admin/distributors/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function getAdminDistributorExportUrl(distributorId: string, type: 'catalog' | 'quotes' | 'reps'): string {
  return `${API_BASE_URL}/api/v1/admin/distributors/${distributorId}/export?type=${type}`;
}

// P0-2b: grant_free_quotes stays increment-only BE-side (ruling). A negative
// amount decrements via the same +=. Callers (e.g. the "Set quota" control)
// compute the delta between a target quota and the current effective_quota
// and pass that delta as amount. Every call writes an AdminAction audit row
// BE-side.
export async function grantFreeQuotes(
  distributorId: string,
  amount: number
): Promise<ApiResponse<{ bonus_free_quotes: number }>> {
  return fetchWithAuth(`/api/v1/admin/distributors/${distributorId}/grant_free_quotes`, {
    method: 'POST',
    body: JSON.stringify({ amount }),
  });
}

export async function downloadDistributorExport(distributorId: string, type: 'catalog' | 'quotes' | 'reps'): Promise<void> {
  const token = getAuthToken();
  const response = await fetchWithPolicy(getAdminDistributorExportUrl(distributorId, type), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Download failed' }));
    throw new Error(err.error || 'Download failed');
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition');
  const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `export-${type}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export interface AdminRestaurantDetail {
  id: string;
  name: string;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  website: string | null;
  status: string;
  created_at: string;
  google_place_id: string | null;
  address_verified: boolean;
  restaurant_admin_id: string | null;
  restaurant_admin_name: string | null;
  restaurant_group: { id: string; name: string } | null;
  contacts: Array<{
    id: string;
    first_name: string;
    last_name: string;
    role: string | null;
    email: string | null;
    phone: string | null;
    is_primary: boolean;
  }>;
  recent_quotes: Array<{
    id: string;
    status: string;
    working_label: string | null;
    created_at: string;
  }>;
}

// ============= ADMIN USERS SINGLE =============

export async function getAdminUser(id: string): Promise<ApiResponse<AdminUser>> {
  return fetchWithAuth(`/api/v1/admin/users/${id}`);
}

export async function assignDistributorAdmin(
  distributorId: string,
  userId: string,
  confirm?: boolean
): Promise<ApiResponse<{ ok: boolean }>> {
  return fetchWithAuth(`/api/v1/admin/distributors/${distributorId}/assign_admin`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, ...(confirm ? { confirm: true } : {}) }),
  });
}

// B-188 fix: QM-admin "+ Add Rep" — creates a brand new, immediately-active
// rep tied to this distributor (mirrors assignDistributorAdmin's sibling,
// the invite-tab path in Add Admin, but always role=rep).
export interface AddRepResult {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  rep_profile: {
    id: string;
    distributor_id: string;
    phone: string | null;
    is_active: boolean;
  };
  distributor: { id: string; name: string };
}

export async function addRepToDistributor(
  distributorId: string,
  data: { first_name: string; last_name: string; email: string; phone?: string }
): Promise<ApiResponse<AddRepResult>> {
  return fetchWithAuth(`/api/v1/admin/distributors/${distributorId}/add_rep`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============= ADMIN RESTAURANTS =============

export async function getAdminRestaurants(): Promise<ApiResponse<AdminRestaurant[]>> {
  return fetchWithAuth('/api/v1/admin/restaurants');
}

export async function getAdminRestaurant(id: string): Promise<ApiResponse<AdminRestaurantDetail>> {
  return fetchWithAuth(`/api/v1/admin/restaurants/${id}`);
}

// ============= ADMIN RESTAURANT CRAWL TRUTH =============
// D3 restaurant-truth screen. Field names mirror the payload contract
// (D3_restaurant_truth_payload_contract.md) EXACTLY - PR 2 (BE serializer) and
// PR 3 (this FE screen) both code to that document. Two shaping rules from
// the contract: (1) `fetched` identifies WHAT record this payload is about,
// rendered first, always - a wrong binding must be visible on the screen. (2)
// char_count / component_count / source counts are facts, never a quality
// score - the FE must not style them as health.

export interface AdminRestaurantCrawlTruthFetched {
  restaurant_id: string; // raw UUID - behind a deliberate control only
  reference: string; // human reference, e.g. "Rioja - Denver, CO" - the honesty check
  website: string | null;
  as_of: string | null; // when the server built this payload
}

export interface AdminRestaurantCrawlTruthCrawl {
  status: string | null; // already-translated sentence; raw code never shown
  confidence: number | null; // 0..100; null -> "not scored"
  is_restaurant: boolean | null; // tri-state: true/false/null are three distinct states
  source_state: string | null; // translated phrase; null -> "not recorded"
  found_at: string | null; // null -> "not recorded"
  data_flags: string[] | null; // [] or null -> no chips rendered
}

export interface AdminRestaurantCrawlTruthSupersededBy {
  reference: string;
  id: string; // raw UUID - behind a control only
}

export interface AdminRestaurantCrawlTruthSource {
  id: string; // raw UUID - behind a control only
  kind_label: string; // translated; unknown kind -> "Other menu"
  kind_raw: string; // details-control only
  is_recognized_kind: boolean;
  provenance_label: string; // "Crawler" | "Diner photo" | "Rep upload" | "Restaurant direct"
  host: string | null;
  url: string | null; // behind a control / truncated; host is the default glance
  list_date: string | null; // null -> "no date"
  fetched_at: string | null; // null -> "not fetched"
  status_sentence: string; // translated; raw code never shown
  char_count: number; // FACT - 0 is a real value, shown as 0
  truncated: boolean;
  component_count: number; // FACT - 0 when menu_id is null (truthful, not missing)
  is_extracted: boolean;
  extraction_method: string | null;
  source_state: 'active' | 'superseded';
  superseded_at: string | null;
  superseded_by: AdminRestaurantCrawlTruthSupersededBy | null;
}

export interface AdminRestaurantCrawlTruthSourceReporting {
  source_count: number;
  extracted_count: number;
  total_char_count: number;
  truncated_source_count: number;
  unknown_kind_count: number;
  coverage_note: string; // literal disclaimer - printed near the numbers
}

export interface AdminRestaurantCrawlTruth {
  fetched: AdminRestaurantCrawlTruthFetched;
  crawl: AdminRestaurantCrawlTruthCrawl;
  sources: AdminRestaurantCrawlTruthSource[];
  source_reporting: AdminRestaurantCrawlTruthSourceReporting;
}

export async function getAdminRestaurantCrawlTruth(id: string): Promise<ApiResponse<AdminRestaurantCrawlTruth>> {
  return fetchWithAuth(`/api/v1/admin/restaurants/${id}/crawl_truth`);
}

// ============= IMPERSONATE =============

export async function impersonateUser(userId: string): Promise<ApiResponse<{ token: string; user: { id: string; email: string; first_name: string; last_name: string; role: string } }>> {
  return fetchWithAuth(`/api/v1/admin/users/${userId}/impersonate`, { method: 'POST' });
}

// ============= CONFERENCE LEADS =============

export async function getConferenceLeads(): Promise<ApiResponse<ConferenceLead[]>> {
  return fetchWithAuth('/api/v1/admin/conference-leads');
}

export async function createConferenceLead(data: FormData): Promise<ApiResponse<ConferenceLead>> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await fetchWithPolicy(`${API_BASE_URL}/api/v1/admin/conference-leads`, {
      method: 'POST',
      headers,
      body: data,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || `Request failed (${response.status})` };
    }

    const result = await response.json();
    return { data: result };
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Network error' };
  }
}

export async function updateConferenceLead(
  id: string,
  data: Partial<ConferenceLead>
): Promise<ApiResponse<ConferenceLead>> {
  return fetchWithAuth(`/api/v1/admin/conference-leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function convertConferenceLead(
  id: string,
  convertTo: string
): Promise<ApiResponse<ConferenceLead>> {
  return fetchWithAuth(`/api/v1/admin/conference-leads/${id}/convert`, {
    method: 'POST',
    body: JSON.stringify({ convert_to: convertTo }),
  });
}

export async function ocrConferenceCard(
  file: File
): Promise<ApiResponse<{ company_name: string; contact_name: string; contact_email: string; contact_phone: string; contact_title: string }>> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const formData = new FormData();
  formData.append('card_photo', file);

  try {
    const response = await fetchWithPolicy(`${API_BASE_URL}/api/v1/admin/conference-leads/ocr`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || `Request failed (${response.status})` };
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Network error' };
  }
}

export async function deleteConferenceLead(id: string): Promise<ApiResponse<void>> {
  return fetchWithAuth(`/api/v1/admin/conference-leads/${id}`, {
    method: 'DELETE',
  });
}

// ============= CONFERENCE EMAIL =============

export interface ConferenceEmailTemplate {
  id?: string;
  conference_name: string;
  subject_line: string;
  greeting: string;
  body_text: string;
  sign_off: string;
}

export async function getConferenceEmailTemplate(conferenceName: string): Promise<ApiResponse<ConferenceEmailTemplate>> {
  return fetchWithAuth(`/api/v1/admin/conference-leads/email_template?conference_name=${encodeURIComponent(conferenceName)}`);
}

export async function saveConferenceEmailTemplate(template: ConferenceEmailTemplate): Promise<ApiResponse<ConferenceEmailTemplate>> {
  return fetchWithAuth('/api/v1/admin/conference-leads/save_email_template', {
    method: 'POST',
    body: JSON.stringify({ template }),
  });
}

export async function sendConferenceLeadEmail(
  leadId: string,
  email: { to: string; subject_line: string; greeting: string; body_text: string; sign_off: string }
): Promise<ApiResponse<ConferenceLead>> {
  return fetchWithAuth(`/api/v1/admin/conference-leads/${leadId}/send_email`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// ============= ADMIN BRANDS =============

export async function getAdminBrands(): Promise<ApiResponse<AdminBrand[]>> {
  return fetchWithAuth('/api/v1/admin/brands');
}

export async function createAdminBrand(data: {
  name: string;
}): Promise<ApiResponse<AdminBrand>> {
  return fetchWithAuth('/api/v1/admin/brands', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAdminBrand(id: string): Promise<ApiResponse<AdminBrandDetail>> {
  return fetchWithAuth(`/api/v1/admin/brands/${id}`);
}

// ============= DISTRIBUTOR SEARCH (public) =============

export async function searchDistributors(query: string): Promise<ApiResponse<Array<{ id: string; name: string; logo_url?: string }>>> {
  return fetchWithAuth(`/api/v1/distributors/search?q=${encodeURIComponent(query)}`);
}

// ============= MATCHING ENGINE =============

export interface MatchingEngineRules {
  sauce_expansions: Array<{
    id: string;
    sauce_name: string;
    components: string[];
    default_behavior: string;
    prepared_sku_blocked: boolean;
    created_at: string;
    updated_at: string;
  }>;
  protein_families: Array<{
    family: string;
    terms: string[];
    locked_species: string[];
  }>;
  cocktail_locks: Array<{
    term: string;
    canonical: string;
    thesaurus_id: string | null;
  }>;
  wine_protected: {
    class_a: Array<{ term: string; notes: string | null; rule_id: string | null }>;
    class_b: Array<{ term: string }>;
    class_c: Array<{ term: string }>;
  };
  chef_beverage: Array<{
    term: string;
    blocked: string[];
    notes: string | null;
    rule_id: string | null;
  }>;
  format_gates: Array<{
    id: string;
    ingredient_pattern: string;
    format_tag: string;
    blocked_in_roles: string[];
    prep_compatibility: string[];
    created_at: string;
    updated_at: string;
  }>;
  synonym_families: Array<{
    id: string;
    canonical_name: string;
    category: string;
    synonyms: string[];
    created_at: string;
    updated_at: string;
  }>;
  identity_locks: Array<{
    id: string;
    ingredient_pattern: string;
    dish_name: string;
    sensitivity: string;
    notes: string | null;
    created_at: string;
    updated_at: string;
  }>;
  match_corrections: Array<{
    id: string;
    ingredient_name: string;
    correction_type: string;
    original_product: { id: string; name: string; brand: string } | null;
    corrected_product: { id: string; name: string; brand: string } | null;
    quote_id: string;
    user: { id: string; name: string } | null;
    promoted: boolean;
    created_at: string;
  }>;
}

export interface MatchingEngineLog {
  id: string;
  user: { id: string; name: string } | null;
  message: string;
  response: string;
  rules_applied: string[];
  source: string;
  created_at: string;
}

export interface RuleValidation {
  status: 'pass' | 'warn' | 'reject';
  rules: Record<string, unknown>[];
  warnings: string[];
  rejections: string[];
}

export interface ChatResponse {
  confirmation: string;
  rules_applied: string[];
  validation?: RuleValidation;
  timestamp: string;
}

export interface SaveRulesResponse {
  status: string;
  rules_applied: string[];
}

export async function getMatchingEngineRules(): Promise<ApiResponse<MatchingEngineRules>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/rules');
}

export async function updateMatchingEngineRule(
  type: string,
  id: string,
  data: Record<string, unknown>
): Promise<ApiResponse<{ status: string }>> {
  return fetchWithAuth(`/api/v1/admin/matching-engine/rules/${type}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteMatchingEngineRule(
  type: string,
  id: string
): Promise<ApiResponse<{ status: string }>> {
  return fetchWithAuth(`/api/v1/admin/matching-engine/rules/${type}/${id}`, {
    method: 'DELETE',
  });
}

export async function promoteCorrection(
  id: string
): Promise<ApiResponse<{ status: string; rule_id: string }>> {
  return fetchWithAuth(`/api/v1/admin/matching-engine/promote/${id}`, {
    method: 'POST',
  });
}

export async function sendMatchingEngineChat(
  message: string,
  file?: File
): Promise<ApiResponse<ChatResponse>> {
  if (file) {
    // Use FormData for file uploads — must NOT set Content-Type header
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('message', message);
    formData.append('file', file);
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetchWithPolicy(`${API_BASE_URL}/api/v1/admin/matching-engine/chat`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      return { data: null, error: errBody.error || `HTTP ${response.status}` };
    }
    const data = await response.json();
    return { data, error: null };
  }
  return fetchWithAuth('/api/v1/admin/matching-engine/chat', {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

export async function saveMatchingEngineRules(
  rules: Record<string, unknown>[]
): Promise<ApiResponse<SaveRulesResponse>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/save-rules', {
    method: 'POST',
    body: JSON.stringify({ rules }),
  });
}

export async function getMatchingEngineLogs(params?: {
  source?: string;
  limit?: number;
}): Promise<ApiResponse<MatchingEngineLog[]>> {
  const searchParams = new URLSearchParams();
  if (params?.source) searchParams.set('source', params.source);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const qs = searchParams.toString();
  return fetchWithAuth(`/api/v1/admin/matching-engine/logs${qs ? `?${qs}` : ''}`);
}

export function getMatchingEngineExportUrl(): string {
  return `${API_BASE_URL}/api/v1/admin/matching-engine/export`;
}

export interface ConceptLabel {
  label: string;
  concept: string;
  cuisine: string | null;
  format: string;
  has_profile: boolean;
  strong_fit_count: number;
  likely_fit_count: number;
  manual_count: number;
}

export interface ConceptsResponse {
  labels: ConceptLabel[];
  profile_concepts: string[];
  total_labels: number;
  total_profiles: number;
}

export interface ConceptTestResult {
  input: string;
  is_concept: boolean;
  profile: { concept: string; cuisine: string | null; format: string } | null;
  profile_data?: {
    strong_fit: string[];
    likely_fit: string[];
    manual: Array<{ name: string; reason: string; status: string }>;
  };
}

export async function getMatchingEngineConcepts(): Promise<ApiResponse<ConceptsResponse>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/concepts');
}

export async function testMatchingEngineConcept(
  input: string
): Promise<ApiResponse<ConceptTestResult>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/test-concept', {
    method: 'POST',
    body: JSON.stringify({ input }),
  });
}

// ============= ADMIN STOCK QUOTES =============

export interface AdminStockQuote {
  id: string;
  name: string;
  restaurant_type: string | null;
  is_system: boolean;
  status: string;
  quote_data: Record<string, unknown> | null;
  dish_count: number;
  component_count: number;
  created_by: { id: string; name: string } | null;
  created_at: string;
  updated_at: string;
}

export async function getAdminStockQuotes(): Promise<ApiResponse<AdminStockQuote[]>> {
  return fetchWithAuth('/api/v1/admin/stock-quotes');
}

export async function createAdminStockQuote(data: {
  name: string;
  restaurant_type: string;
  quote_data?: Record<string, unknown>;
}): Promise<ApiResponse<AdminStockQuote>> {
  return fetchWithAuth('/api/v1/admin/stock-quotes', {
    method: 'POST',
    body: JSON.stringify({ stock_quote: data }),
  });
}

export async function updateAdminStockQuote(
  id: string,
  data: Partial<{ name: string; restaurant_type: string; status: string; quote_data: Record<string, unknown> }>
): Promise<ApiResponse<AdminStockQuote>> {
  return fetchWithAuth(`/api/v1/admin/stock-quotes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ stock_quote: data }),
  });
}

export async function deleteAdminStockQuote(id: string): Promise<ApiResponse<void>> {
  return fetchWithAuth(`/api/v1/admin/stock-quotes/${id}`, {
    method: 'DELETE',
  });
}

// ═══════════════════════════════════════════════════════════════════════
// Matching Diagnostics (Fix 119)
// ═══════════════════════════════════════════════════════════════════════

export interface DiagnosticResult {
  input: {
    component_name: string;
    normalized_name: string;
    inferred_category: string;
    adjacency_allowed?: string[];
    absurd_blocked?: string[];
    synonyms_expanded: string[];
    synonym_family: string | null;
    detected_format: string | null;
    identity_locked: boolean;
    identity_semi_locked: boolean;
  };
  retrieval_guard: {
    synonym_text_matches: { count: number; token_fallback_used?: boolean; token_fallback_tokens?: string[]; top_10: ProductBrief[] };
    // Fix 125: absurd_category_block replaces category_gating
    absurd_category_block?: {
      component_category: string;
      absurd_pairs: string[];
      adjacent_categories: string[];
      survived: number;
      removed: Array<{ product: ProductBrief; reason: string }>;
      note: string;
    };
    // Legacy field for backward compat
    category_gating?: {
      component_category: string;
      allowed_categories: string[];
      survived: number;
      removed: Array<{ product: ProductBrief; reason: string }>;
    };
    format_blocking: {
      detected_format: string | null;
      blocked_patterns: string[];
      survived: number;
      removed: Array<{ product: ProductBrief; reason: string }>;
    };
    name_format_blocking: {
      survived: number;
      removed: Array<{ product: ProductBrief; reason: string }>;
    };
    prep_state_gate?: {
      is_raw_component: boolean;
      survived: number;
      removed: Array<{ product: ProductBrief; reason: string }>;
    };
    final_candidates: ProductBrief[];
  };
  fallback_path: {
    triggered: boolean;
    reason?: string;
    guard_fallback_fired?: boolean;
    guard_fallback_count?: number;
    absurd_blocked_fallback?: boolean;
    absurd_pairs_excluded?: string[];
    category_constrained_fallback?: boolean;
    searched_categories?: string[];
    pool_size?: number;
    candidates_found?: ProductBrief[];
    clean_miss?: boolean;
    note?: string;
  };
  scoring: {
    sensitivity: string;
    role: string;
    identity_family: string | null;
    locked: boolean;
    floor: number;
    candidates: ScoredCandidate[];
    note?: string;
  };
  final_result: {
    match: {
      product_name: string;
      brand: string;
      category: string;
      score: number;
      quality: string;
    } | null;
    reason?: string;
    alternates_count?: number;
    total_scored?: number;
    total_above_floor?: number;
    best_score?: number;
  };
  active_rule_count?: number;
  active_rules?: TeachExistingRule[];
}

// ═══════════════════════════════════════════════════════════════════════
// Fix 127: Teach mode types
// ═══════════════════════════════════════════════════════════════════════

export interface TeachUserPick {
  position: number;
  product_id: string;
}

export interface TeachGap {
  position: number;
  already_correct: boolean;
  user_pick: { product_id: string; product_name: string; brand: string; engine_rank: number | null; engine_score: number };
  engine_pick: { product_id: string; product_name: string; brand: string; engine_rank: number; engine_score: number } | null;
  score_delta: number;
  factor_deltas: Record<string, number>;
}

export interface TeachProposedRule {
  rule_type: string;
  target_name: string;
  replacement_name: string | null;
  reason: string;
  affects_positions: number[];
}

export interface TeachPreviewCandidate {
  product_id: string;
  product_name: string;
  brand: string;
  category?: string;
  normalized_category?: string;
  original_score: number;
  adjusted_score: number;
  original_rank: number;
  new_rank: number;
  rules_applied: string[];
}

export interface TeachConflict {
  type: 'contradiction' | 'stacking' | 'identity_lock_override';
  new_rule: TeachProposedRule;
  existing_rule?: TeachExistingRule;
  existing_count?: number;
  existing_rules?: TeachExistingRule[];
  message: string;
  warning?: boolean;
}

export interface TeachExistingRule {
  id: string;
  rule_type: string;
  target_name: string;
  replacement_name?: string | null;
  reason?: string;
  source?: string;
  active?: boolean;
  created_at?: string;
}

export interface TeachResult {
  gaps: { gaps: TeachGap[]; agreement_count: number; total_positions: number };
  proposed_rules: TeachProposedRule[];
  explanation: string;
  preview: { candidates: TeachPreviewCandidate[] };
  conflicts: TeachConflict[];
}

export interface TeachApplyResult {
  saved_rule_ids: string[];
  confirmed_result: DiagnosticResult;
}

export async function runTeachAnalysis(
  component: string,
  catalogId: string,
  userRanking: TeachUserPick[],
  engineCandidates: ScoredCandidate[],
  category?: string
): Promise<ApiResponse<TeachResult>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/teach', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      component,
      catalog_id: catalogId,
      category,
      user_ranking: userRanking,
      engine_candidates: engineCandidates
    })
  });
}

export async function runTeachPreview(
  component: string,
  catalogId: string,
  rules: TeachProposedRule[],
  engineCandidates: ScoredCandidate[]
): Promise<ApiResponse<{ preview: { candidates: TeachPreviewCandidate[] }; conflicts: TeachConflict[] }>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/teach-preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      component,
      catalog_id: catalogId,
      rules,
      engine_candidates: engineCandidates
    })
  });
}

export async function applyTeachRules(
  component: string,
  catalogId: string,
  rules: TeachProposedRule[],
  userRanking: TeachUserPick[],
  beforeRanking: ScoredCandidate[],
  category?: string
): Promise<ApiResponse<TeachApplyResult>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/teach-apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      component,
      catalog_id: catalogId,
      category,
      rules,
      user_ranking: userRanking,
      before_ranking: beforeRanking
    })
  });
}

export async function undoTeachSession(ruleIds: string[]): Promise<ApiResponse<{ success: boolean; undone_count: number }>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/teach-undo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rule_ids: ruleIds })
  });
}

export interface ProductBrief {
  product_id: string;
  item_number: string;
  brand: string;
  product_name: string;
  canonical_product: string | null;
  category: string;
  normalized_category?: string;
  pack_size: string | null;
  prep_state: string | null;
  search_text: string | null;
}

export interface ScoredCandidate {
  product_id: string;
  product_name: string;
  brand: string;
  category: string;
  normalized_category?: string;
  pack_size: string | null;
  prep_state: string | null;
  scores: {
    exact_identity: number;
    name_similarity: number;
    category_fit: number;
    role_fit: number;
    keyword_overlap: number;
    pack_plausibility: number;
    concept_fit: number;
    format_fit: number;
    name_boost: number;
    category_boost: number;
    total: number;
  };
  above_floor: boolean;
}

export interface DiagnosticCatalog {
  id: string;
  distributor_name: string;
  product_count: number;
  is_demo: boolean;
  created_at: string;
}

export async function getDiagnosticsEnabled(): Promise<ApiResponse<{ enabled: boolean }>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/diagnostics-enabled');
}

export async function getDiagnosticsCatalogs(): Promise<ApiResponse<{ catalogs: DiagnosticCatalog[] }>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/diagnostics-catalogs');
}

export async function runDiagnostic(
  component: string,
  catalogId: string,
  category?: string
): Promise<ApiResponse<DiagnosticResult>> {
  const qs = new URLSearchParams({ component, catalog_id: catalogId });
  if (category) qs.set('category', category);
  return fetchWithAuth(`/api/v1/admin/matching-engine/diagnose?${qs}`);
}

// ============= ADMIN CATALOG CLASSIFICATION =============

export interface AdminCatalogStats {
  id: string;
  distributor_name: string;
  total_products: number;
  by_category: Record<string, number>;
  classification_status: string;
  classification_progress: number;
  classification_total: number;
}

export interface AdminCatalogProduct {
  id: string;
  item_number: string;
  brand: string;
  product_name: string;
  pack_size: string;
  category: string;
  subcategory: string | null;
  standard_subcategory: string | null;
  normalized_category: string | null;
  category_source: string;
  ai_confidence: string | null;
}

export interface AdminCatalogProductsResponse {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  brands: string[];
  products: AdminCatalogProduct[];
}

export async function getAdminCatalogStats(catalogId: string): Promise<ApiResponse<AdminCatalogStats>> {
  return fetchWithAuth(`/api/v1/admin/matching-engine/catalog-stats?catalog_id=${catalogId}`);
}

export async function getAdminCatalogProducts(
  catalogId: string, page = 1, perPage = 50, opts?: { category?: string; search?: string; brand?: string }
): Promise<ApiResponse<AdminCatalogProductsResponse>> {
  const params = new URLSearchParams({ catalog_id: catalogId, page: String(page), per_page: String(perPage) });
  if (opts?.category) params.set('category', opts.category);
  if (opts?.search) params.set('search', opts.search);
  if (opts?.brand) params.set('brand', opts.brand);
  return fetchWithAuth(`/api/v1/admin/matching-engine/catalog-products?${params}`);
}

export async function adminReclassifyOthers(catalogId: string): Promise<ApiResponse<{ message: string; other_count: number }>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/catalog-reclassify', {
    method: 'POST',
    body: JSON.stringify({ catalog_id: catalogId }),
  });
}

export async function adminBulkUpdateCategory(catalogId: string, productIds: string[], category: string): Promise<ApiResponse<{ updated: number; category: string }>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/catalog-bulk-update', {
    method: 'PATCH',
    body: JSON.stringify({ catalog_id: catalogId, product_ids: productIds, category }),
  });
}

export async function adminBulkUpdateSubcategory(catalogId: string, productIds: string[], standard_subcategory: string): Promise<ApiResponse<{ updated: number; standard_subcategory: string }>> {
  return fetchWithAuth('/api/v1/admin/matching-engine/catalog-bulk-update', {
    method: 'PATCH',
    body: JSON.stringify({ catalog_id: catalogId, product_ids: productIds, standard_subcategory }),
  });
}

// ============= BRAND RULES =============

export interface BrandRule {
  id: string;
  brand_name: string;
  rule_type: 'lock' | 'bias' | 'none';
  category: string;
  is_active: boolean;
  notes: string | null;
  product_count: number;
  category_distribution: Record<string, number>;
  last_audited_at: string | null;
  /** Brand-rule hierarchy. All three are serialized by
   *  admin/brand_rules_controller.rb:137-139 and were simply not declared
   *  here, so QMAdminBrandRules read them through `as any` casts. They are
   *  declared now because the delete confirm has to state what deleting a
   *  parent does to the variants under it: BrandRule
   *  `has_many :child_brands, dependent: :nullify`, so those rows survive but
   *  are silently detached. */
  parent_brand_id: string | null;
  parent_brand_name: string | null;
  child_brands: Array<{ id: string; brand_name: string }>;
  created_at: string;
}

export interface BrandAuditResult {
  total_brands: number;
  locks: number;
  biases: number;
  brands: Array<{
    brand: string;
    product_count: number;
    categories: string[];
    suggested_type: string;
    suggested_category: string;
    existing_rule: BrandRule | null;
  }>;
}

export async function getAdminBrandRules(params?: { rule_type?: string; q?: string }): Promise<ApiResponse<BrandRule[]>> {
  const query = new URLSearchParams();
  if (params?.rule_type) query.set('rule_type', params.rule_type);
  if (params?.q) query.set('q', params.q);
  const qs = query.toString();
  return fetchWithAuth(`/api/v1/admin/brand-rules${qs ? '?' + qs : ''}`);
}

export async function createAdminBrandRule(data: { brand_name: string; rule_type: string; category: string; notes?: string }): Promise<ApiResponse<BrandRule>> {
  return fetchWithAuth('/api/v1/admin/brand-rules', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateAdminBrandRule(id: string, data: Partial<BrandRule>): Promise<ApiResponse<BrandRule>> {
  return fetchWithAuth(`/api/v1/admin/brand-rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function deleteAdminBrandRule(id: string): Promise<ApiResponse<{ id: string; deleted: boolean }>> {
  return fetchWithAuth(`/api/v1/admin/brand-rules/${id}`, { method: 'DELETE' });
}

export async function auditBrandRules(distributorId?: string): Promise<ApiResponse<BrandAuditResult>> {
  const body = distributorId ? JSON.stringify({ distributor_id: distributorId }) : '{}';
  return fetchWithAuth('/api/v1/admin/brand-rules/audit', { method: 'POST', body });
}

export async function seedBrandRules(): Promise<ApiResponse<{ seeded: number; total: number }>> {
  return fetchWithAuth('/api/v1/admin/brand-rules/seed', { method: 'POST' });
}

// ============= ADMIN HEALTH =============

export interface HealthCheck {
  overall: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  services: Record<string, {
    name: string;
    status: 'ok' | 'warning' | 'error';
    message: string;
  }>;
}

export async function getAdminHealth(): Promise<ApiResponse<HealthCheck>> {
  return fetchWithAuth('/api/v1/admin/health');
}

export interface HealthHistoryEntry {
  status: 'ok' | 'warning' | 'error';
  message: string;
  checked_at: string;
}

export interface HealthServiceHistory {
  name: string;
  current_status: 'ok' | 'warning' | 'error';
  history: HealthHistoryEntry[];
  last_incident: string | null;
}

export interface HealthHistory {
  services: Record<string, HealthServiceHistory>;
}

export async function getAdminHealthHistory(): Promise<ApiResponse<HealthHistory>> {
  return fetchWithAuth('/api/v1/admin/health/history');
}

// ============= EXCLUSION RULES =============

export interface ExclusionRule {
  id: string;
  word: string;
  from_category: string;
  to_category: string;
  source: 'manual' | 'suggested' | 'approved';
  active: boolean;
  products_affected: number;
  confidence: number;
  created_at: string;
}

export interface AuditLogEntry {
  id: string;
  product_name: string;
  from_category: string;
  to_category: string;
  rule_word: string;
  applied_by: string;
  created_at: string;
}

export interface ExclusionRulesResponse {
  active_rules: Record<string, ExclusionRule[]>;
  suggested_rules: ExclusionRule[];
  recent_audit_log: AuditLogEntry[];
}

export async function getExclusionRules(): Promise<ApiResponse<ExclusionRulesResponse>> {
  return fetchWithAuth('/api/v1/admin/exclusion_rules');
}

export async function createExclusionRule(data: {
  word: string;
  from_category: string;
  to_category: string;
  apply_now?: boolean;
}): Promise<ApiResponse<ExclusionRule>> {
  return fetchWithAuth('/api/v1/admin/exclusion_rules', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateExclusionRule(
  id: string,
  data: { word?: string; from_category?: string; to_category?: string }
): Promise<ApiResponse<ExclusionRule>> {
  return fetchWithAuth(`/api/v1/admin/exclusion_rules/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteExclusionRule(id: string): Promise<ApiResponse<{ message: string }>> {
  return fetchWithAuth(`/api/v1/admin/exclusion_rules/${id}`, {
    method: 'DELETE',
  });
}

export async function approveExclusionRule(id: string): Promise<ApiResponse<ExclusionRule>> {
  return fetchWithAuth(`/api/v1/admin/exclusion_rules/${id}/approve`, {
    method: 'POST',
  });
}

export async function rejectExclusionRule(id: string): Promise<ApiResponse<{ message: string }>> {
  return fetchWithAuth(`/api/v1/admin/exclusion_rules/${id}/reject`, {
    method: 'POST',
  });
}

export async function applyAllExclusionRules(): Promise<ApiResponse<{ moved: number; log: AuditLogEntry[] }>> {
  return fetchWithAuth('/api/v1/admin/exclusion_rules/apply_all', {
    method: 'POST',
  });
}

// ============= CLUSTER LABELS =============

export interface ClusterLabel {
  id: string;
  cluster_id: string | null;
  canonical_product: string | null;
  product_family: string | null;
  category: string | null;
  form_type: string | null;
  compound_type: 'identity' | 'modified' | 'true' | null;
  identity_flags: Record<string, unknown>;
  confidence: number | null;
  status: string;
  prompt_version: string;
  model_id: string;
  created_at: string;
  updated_at: string;
}

export interface ClusterLabelUpdate {
  category?: string | null;
  form_type?: string | null;
  compound_type?: 'identity' | 'modified' | 'true' | null;
  identity_flags?: Record<string, unknown>;
}

export type ClusterLabelReasonCode =
  | 'misclassified_category'
  | 'wrong_form_type'
  | 'identity_lock_fix'
  | 'compound_type_correction'
  | 'admin_seeded_truth'
  | 'merge_consolidation'
  | 'admin_rollback'
  | 'other';

export interface ClusterLabelHighImpact {
  is_high_impact: boolean;
  member_count: number;
  product_count: number;
  is_umbrella: boolean;
  reasons: string[];
}

export interface ClusterLabelAuditLogEntry {
  id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  reason: string | null;
  reason_code: ClusterLabelReasonCode | null;
  changed_at: string;
  changed_by_user_id: string;
  before_snapshot: Record<string, unknown>;
  after_snapshot: Record<string, unknown>;
}

export interface ClusterLabelDetail {
  cluster_label: ClusterLabel;
  high_impact: ClusterLabelHighImpact;
  recent_audit_logs: ClusterLabelAuditLogEntry[];
}

export interface ClusterLabelUpdatePayload {
  fields: ClusterLabelUpdate;
  reason_code: ClusterLabelReasonCode;
  reason?: string;
  confirm_warnings?: boolean;
}

export interface ClusterLabelWarning {
  field: string;
  message: string;
}

// v1 — kept for backward compatibility
export async function updateClusterLabel(
  id: string,
  fields: ClusterLabelUpdate,
  reason?: string
): Promise<ApiResponse<ClusterLabel>> {
  return fetchWithAuth(`/api/v1/admin/cluster_labels/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ cluster_label: fields, reason }),
  });
}

export async function getClusterLabel(id: string): Promise<ApiResponse<ClusterLabelDetail>> {
  return fetchWithAuth(`/api/v1/admin/cluster_labels/${id}`);
}

// fetchWithAuth returns { error } on non-2xx, discarding other fields like `warnings`.
// For the soft-validation path we need to retain `warnings` from the 422 body.
// This wrapper reads the raw response body and surfaces both error and warnings.
export async function updateClusterLabelV2(
  id: string,
  payload: ClusterLabelUpdatePayload
): Promise<ApiResponse<ClusterLabelDetail> & { warnings?: ClusterLabelWarning[] }> {
  const token = localStorage.getItem('quoteme_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await fetchWithPolicy(`${API_BASE_URL}/api/v1/admin/cluster_labels/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({
        cluster_label: payload.fields,
        reason_code: payload.reason_code,
        reason: payload.reason,
        confirm_warnings: payload.confirm_warnings,
      }),
    });

    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        error: body.error || `Request failed (${response.status})`,
        warnings: body.warnings || undefined,
      };
    }

    return { data: body as ClusterLabelDetail };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Network error' };
  }
}

export async function rollbackClusterLabel(
  id: string,
  auditLogId: string,
  reason?: string
): Promise<ApiResponse<ClusterLabelDetail>> {
  return fetchWithAuth(`/api/v1/admin/cluster_labels/${id}/rollback`, {
    method: 'POST',
    body: JSON.stringify({ audit_log_id: auditLogId, reason }),
  });
}

export interface ClusterLabelListRow {
  id: string;
  canonical_product: string | null;
  category: string | null;
  form_type: string | null;
  compound_type: 'identity' | 'modified' | 'true' | null;
  confidence: number | null;
  status: string;
  created_via: string;
  identity_flags_count: number;
  last_edited: string;
  has_been_edited: boolean;
}

export interface ClusterLabelListPriorityCounts {
  recently_edited: number;
  umbrella: number;
  high_product_count: number;
  low_confidence: number;
  recently_rolled_back: number;
}

export interface ClusterLabelListResponse {
  rows: ClusterLabelListRow[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  priority_counts: ClusterLabelListPriorityCounts;
}

export interface ClusterLabelListFilters {
  q?: string;
  category?: string;
  form_type?: string;
  compound_type?: 'identity' | 'modified' | 'true' | 'null';
  created_via?: string;
  has_been_edited?: boolean;
  recently_edited?: boolean;
  page?: number;
  per_page?: number;
  sort?: string; // e.g. 'canonical_product_asc', 'last_edited_desc'
}

export interface BulkUpdatePayload {
  ids: string[];
  updates: {
    category?: string | null;
    form_type?: string | null;
    compound_type?: 'identity' | 'modified' | 'true' | null;
    identity_flags?: Record<string, unknown>;
  };
  reason_code: ClusterLabelReasonCode;
  reason?: string;
  confirm_warnings?: boolean;
}

export interface BulkUpdateResult {
  updated: { id: string; fields_changed: string[] }[];
  unchanged: string[];
  failed: { id: string; error: string }[];
}

export interface BulkUpdateWarning {
  id: string;
  field: string;
  message: string;
}

export async function bulkUpdateClusterLabels(
  payload: BulkUpdatePayload
): Promise<ApiResponse<BulkUpdateResult> & { warnings?: BulkUpdateWarning[] }> {
  const token = localStorage.getItem('quoteme_token');
  const res = await fetchWithPolicy(`${API_BASE_URL}/api/v1/admin/cluster_labels/bulk_update`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (res.ok) return { data: body };
  return body; // includes error + warnings if present
}

export async function listClusterLabels(
  filters: ClusterLabelListFilters = {}
): Promise<ApiResponse<ClusterLabelListResponse>> {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.category) params.set('category', filters.category);
  if (filters.form_type) params.set('form_type', filters.form_type);
  if (filters.compound_type) params.set('compound_type', filters.compound_type);
  if (filters.created_via) params.set('created_via', filters.created_via);
  if (filters.has_been_edited) params.set('has_been_edited', 'true');
  if (filters.recently_edited) params.set('recently_edited', 'true');
  if (filters.page) params.set('page', String(filters.page));
  if (filters.per_page) params.set('per_page', String(filters.per_page));
  if (filters.sort) params.set('sort', filters.sort);
  const qs = params.toString();
  return fetchWithAuth(`/api/v1/admin/cluster_labels${qs ? `?${qs}` : ''}`);
}

// ============= RESTAURANT GROUPS =============

export interface AdminRestaurantGroup {
  id: string;
  name: string;
  status: string;
  restaurant_count: number;
  created_at: string;
}

export interface AdminRestaurantGroupDetail extends AdminRestaurantGroup {
  restaurants: Array<{
    id: string;
    name: string;
    city: string | null;
    state: string | null;
    status: string;
    contact_count: number;
    created_at: string;
  }>;
}

export async function getAdminRestaurantGroups(): Promise<ApiResponse<AdminRestaurantGroup[]>> {
  return fetchWithAuth('/api/v1/restaurant_groups');
}

export async function getAdminRestaurantGroup(id: string): Promise<ApiResponse<AdminRestaurantGroupDetail>> {
  return fetchWithAuth(`/api/v1/restaurant_groups/${id}`);
}

export async function createAdminRestaurantGroup(data: {
  name: string;
  status?: string;
}): Promise<ApiResponse<AdminRestaurantGroup>> {
  return fetchWithAuth('/api/v1/restaurant_groups', {
    method: 'POST',
    body: JSON.stringify({ restaurant_group: data }),
  });
}

export async function updateAdminRestaurantGroup(
  id: string,
  data: { name?: string; status?: string }
): Promise<ApiResponse<AdminRestaurantGroup>> {
  return fetchWithAuth(`/api/v1/restaurant_groups/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ restaurant_group: data }),
  });
}

export async function addRestaurantToGroup(
  groupId: string,
  data: { restaurant_id: string } | { name: string; city?: string; state?: string }
): Promise<ApiResponse<AdminRestaurantGroupDetail>> {
  return fetchWithAuth(`/api/v1/restaurant_groups/${groupId}/add_restaurant`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============= RESTAURANT CREATE =============

export interface Restaurant {
  id: string;
  name: string;
  address: string | null;
  address_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
  distributor_id: string;
  restaurant_group_id: string | null;
  google_place_id: string | null;
  address_verified: boolean;
  test_seeded: boolean;
  status: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateRestaurantInput {
  name: string;
  city: string;
  state: string;
  country?: string;
  address?: string;
  address_2?: string;
  zip?: string;
  phone?: string;
  email?: string;
  google_place_id?: string;
  restaurant_group_id?: string;
  distributor_id?: string; // only QM admin needs this
}

export interface DuplicateRestaurantError {
  error: string;
  existing_id: string;
}

export async function createRestaurant(
  input: CreateRestaurantInput
): Promise<ApiResponse<Restaurant> & { duplicateId?: string }> {
  const token = localStorage.getItem('quoteme_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await fetchWithPolicy(`${API_BASE_URL}/api/v1/admin/restaurants`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ restaurant: input }),
    });

    if (response.status === 409) {
      const body: DuplicateRestaurantError = await response.json().catch(() => ({ error: 'Duplicate restaurant', existing_id: '' }));
      return { error: body.error || 'Duplicate restaurant', duplicateId: body.existing_id };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || `Request failed (${response.status})` };
    }

    const data: Restaurant = await response.json();
    return { data };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Network error' };
  }
}

// ============= RESTAURANT ADMIN ASSIGNMENT =============

export interface AssignRestaurantAdminNewUserInput {
  email: string;
  first_name: string;
  last_name: string;
}

export interface AssignRestaurantAdminResult {
  restaurant_admin: { id: string; email: string; name: string };
  previous_admin_user_id: string | null;
}

export async function assignRestaurantAdminNewUser(
  restaurantId: string,
  user: AssignRestaurantAdminNewUserInput
): Promise<ApiResponse<AssignRestaurantAdminResult>> {
  return fetchWithAuth(`/api/v1/admin/restaurants/${restaurantId}/restaurant_admin`, {
    method: 'POST',
    body: JSON.stringify({ user }),
  });
}

export async function assignRestaurantAdminExistingUser(
  restaurantId: string,
  userId: string
): Promise<ApiResponse<AssignRestaurantAdminResult>> {
  return fetchWithAuth(`/api/v1/admin/restaurants/${restaurantId}/restaurant_admin`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

// ============= ADMIN RESTAURANT CONTACTS =============

export interface AdminRestaurantContact {
  id: string;
  restaurant_id: string;
  first_name: string;
  last_name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  is_primary: boolean;
}

export interface CreateAdminRestaurantContactInput {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  role?: string;
  is_primary?: boolean;
}

export async function createAdminRestaurantContact(
  restaurantId: string,
  data: CreateAdminRestaurantContactInput
): Promise<ApiResponse<AdminRestaurantContact>> {
  return fetchWithAuth(`/api/v1/admin/restaurants/${restaurantId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============= KNOWLEDGE GAP REVIEW QUEUE =============

export interface KnowledgeGapSubmissionSourceData {
  component_text: string;
  frequency_count: number;
  sample_quote_ids?: string[];
  first_seen?: string;
  last_seen?: string;
  suggested_canonical?: string | null;
  suggested_category?: string | null;
  suggested_form_type?: string | null;
  suggested_compound_type?: 'identity' | 'modified' | 'true' | null;
  commonly_finished_flag?: boolean | null;
  rep_notes?: string | null;
  llm_suggestion?: unknown;
  [key: string]: unknown;
}

export interface KnowledgeGapSubmission {
  id: string;
  submission_type: 'knowledge_gap';
  status: 'pending' | 'approved' | 'archived';
  source_data: KnowledgeGapSubmissionSourceData;
  submitted_by: string | null;
  submitted_at: string;
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  resolved_data: Record<string, unknown> | null;
  // suspected-tail clumping v1 — non-null when SuspectedTailClumpService has grouped
  // this submission into a cluster by TF-IDF cosine similarity (threshold 0.65).
  cluster_id: string | null;
}

export type KnowledgeGapEditedData = {
  suggested_canonical?: string;
  suggested_category?: string;
  suggested_form_type?: string;
  suggested_compound_type?: 'identity' | 'modified' | 'true' | null;
};

export async function listKnowledgeGapSubmissions(
  status: 'pending' | 'approved' | 'archived' = 'pending',
  includeArchived?: boolean
): Promise<ApiResponse<KnowledgeGapSubmission[]>> {
  const params = new URLSearchParams({ status });
  if (includeArchived) params.set('include_archived', 'true');
  return fetchWithAuth(`/api/v1/admin/knowledge_gap_submissions?${params.toString()}`);
}

export async function approveKnowledgeGapSubmission(
  id: string,
  editedData?: KnowledgeGapEditedData,
  reviewNotes?: string
): Promise<ApiResponse<KnowledgeGapSubmission>> {
  return fetchWithAuth(`/api/v1/admin/knowledge_gap_submissions/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ edited_data: editedData, review_notes: reviewNotes }),
  });
}

// Renamed from rejectKnowledgeGapSubmission (v2.2 archive semantics).
// G4: this function only appears on KGF surfaces -- safe to rename.
export async function archiveKnowledgeGapSubmission(
  id: string,
  reviewNotes?: string
): Promise<ApiResponse<KnowledgeGapSubmission>> {
  return fetchWithAuth(`/api/v1/admin/knowledge_gap_submissions/${id}/archive`, {
    method: 'POST',
    body: JSON.stringify({ review_notes: reviewNotes }),
  });
}

export async function restoreKnowledgeGapSubmission(
  id: string
): Promise<ApiResponse<KnowledgeGapSubmission>> {
  return fetchWithAuth(`/api/v1/admin/knowledge_gap_submissions/${id}/restore`, {
    method: 'POST',
  });
}

// ── Knowledge Gap Filler V2 ────────────────────────────────────────────────────

export interface KnowledgeGapSubmissionV2 extends KnowledgeGapSubmission {
  // source_data shape extended with placement + parent fields
}

export interface ParentClusterLabelResult {
  id: string;
  canonical_product: string;
  category: string | null;
}

export async function approveKnowledgeGapAsTail(
  id: string,
  parentClusterLabelId: string,
  reviewNotes?: string
): Promise<ApiResponse<KnowledgeGapSubmission>> {
  return fetchWithAuth(`/api/v1/admin/knowledge_gap_submissions/${id}/approve_as_tail`, {
    method: 'POST',
    body: JSON.stringify({ parent_cluster_label_id: parentClusterLabelId, review_notes: reviewNotes }),
  });
}

export async function setKnowledgeGapPlacement(
  id: string,
  placement: 'tail_to_existing_head' | 'new_head',
  suggestedParentId?: string
): Promise<ApiResponse<KnowledgeGapSubmission>> {
  return fetchWithAuth(`/api/v1/admin/knowledge_gap_submissions/${id}/set_placement`, {
    method: 'POST',
    body: JSON.stringify({ placement, suggested_parent_id: suggestedParentId }),
  });
}

export async function searchClusterLabelParents(
  query: string,
  limit: number = 20
): Promise<ApiResponse<ParentClusterLabelResult[]>> {
  return fetchWithAuth(
    `/api/v1/admin/knowledge_gap_submissions/parent_search?q=${encodeURIComponent(query)}&limit=${limit}`
  );
}

// ── Knowledge Gap Filler v2.1 ─────────────────────────────────────────────────

export interface BulkApproveAsTailItem {
  id: string;
  parent_cluster_label_id: string;
  review_notes?: string;
}

export interface BulkApproveAsTailResponse {
  approved: string[];
  failed: Array<{ id: string; error: string }>;
}

export async function bulkApproveAsTail(
  approvals: BulkApproveAsTailItem[]
): Promise<ApiResponse<BulkApproveAsTailResponse>> {
  return fetchWithAuth('/api/v1/admin/knowledge_gap_submissions/bulk_approve_as_tail', {
    method: 'POST',
    body: JSON.stringify({ approvals }),
  });
}

export interface BulkArchiveResponse {
  archived: string[];
  failed: Array<{ id: string; error: string }>;
}

export async function bulkArchiveKnowledgeGap(
  ids: string[],
  reviewNotes?: string
): Promise<ApiResponse<BulkArchiveResponse>> {
  return fetchWithAuth('/api/v1/admin/knowledge_gap_submissions/bulk_archive', {
    method: 'POST',
    body: JSON.stringify({ ids, review_notes: reviewNotes }),
  });
}

export interface BulkMoveToHeadResponse {
  moved: string[];
  failed: Array<{ id: string; error: string }>;
}

export async function bulkMoveToHead(
  ids: string[]
): Promise<ApiResponse<BulkMoveToHeadResponse>> {
  return fetchWithAuth('/api/v1/admin/knowledge_gap_submissions/bulk_move_to_head', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export interface KnowledgeGapHistoryRow {
  id: string;
  candidate_text: string | null;
  action: 'approved_as_tail' | 'approved_as_new_head' | 'archived';
  parent_canonical: string | null;
  parent_cluster_label_id: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
}

export interface KnowledgeGapHistoryResponse {
  history: KnowledgeGapHistoryRow[];
  total_count: number;
}

export async function listKnowledgeGapHistory(
  limit: number = 25,
  offset: number = 0
): Promise<ApiResponse<KnowledgeGapHistoryResponse>> {
  return fetchWithAuth(
    `/api/v1/admin/knowledge_gap_submissions/history?limit=${limit}&offset=${offset}`
  );
}

export async function undoKnowledgeGapTail(
  submissionId: string
): Promise<ApiResponse<KnowledgeGapSubmission>> {
  return fetchWithAuth(`/api/v1/admin/knowledge_gap_submissions/${submissionId}/undo_tail`, {
    method: 'POST',
  });
}

// ── Suspected-tail clumping v1 ─────────────────────────────────────────────────

/**
 * Decision log record written after the operator bulk-acts on a cluster.
 * Mirrors the TailClusterDecision BE model (tail_cluster_decisions_controller.rb#serialize).
 * The actual approval/archive mutations happen via bulkApproveAsTail / bulkArchiveKnowledgeGap;
 * this is a separate forensic write so calibration errors don't affect UX.
 */
export interface TailClusterDecision {
  id: string;
  cluster_id: string;
  decision: 'kept' | 'split' | 'rejected';
  cluster_size: number;
  threshold_used: number;
  avg_intra_similarity: number | null;
  submission_ids: string[];
  parent_cluster_label_id: string | null;
  reviewer_user_id: string | null;
  decided_at: string | null;
  review_notes: string | null;
}

export interface RecordTailClusterDecisionParams {
  cluster_id: string;
  decision: 'kept' | 'split' | 'rejected';
  submission_ids: string[];
  threshold_used: number;
  avg_intra_similarity?: number | null;
  parent_cluster_label_id?: string | null;
  review_notes?: string | null;
}

export async function recordTailClusterDecision(
  params: RecordTailClusterDecisionParams
): Promise<ApiResponse<TailClusterDecision>> {
  return fetchWithAuth('/api/v1/admin/tail_cluster_decisions', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// ── QM-Admin Logo Uploads (B-181) ───────────────────────────────────────────

/** POST /api/v1/admin/brands/:id/logo — QM admin uploads logo for a brand. */
export async function uploadAdminBrandLogo(
  brandId: string,
  file: File,
): Promise<ApiResponse<{ logo_url: string }>> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const formData = new FormData();
  formData.append('logo', file);

  try {
    const response = await fetchWithPolicy(`${API_BASE_URL}/api/v1/admin/brands/${brandId}/logo`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      return { error: errBody.error || `HTTP ${response.status}` };
    }
    return { data: await response.json() };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Network error' };
  }
}

/** POST /api/v1/admin/distributors/:id/logo — QM admin uploads logo for a distributor. */
export async function uploadAdminDistributorLogo(
  distributorId: string,
  file: File,
): Promise<ApiResponse<{ logo_url: string }>> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const formData = new FormData();
  formData.append('logo', file);

  try {
    const response = await fetchWithPolicy(`${API_BASE_URL}/api/v1/admin/distributors/${distributorId}/logo`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      return { error: errBody.error || `HTTP ${response.status}` };
    }
    return { data: await response.json() };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Network error' };
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Operational Memory Milestone 0 — Gap Filler "Needs Your Pick" (OME-M0)
// ═══════════════════════════════════════════════════════════════════════
//
// Distinct from the Knowledge Gap Filler (corpus/ontology tail-clumping tool
// above). This surfaces components the matching engine could NOT confidently
// resolve on live quotes -- deduped by (distributor_id, canonical_key) and
// frequency-ranked by the BE.
//
// READ-ONLY. There is no pick endpoint. The routes are GET
// admin/gap_filler_needs_pick and GET admin/gap_filler_needs_pick/:id/quotes,
// and this file exports only the two matching getters. The original wording
// here ("so an admin can pick the right catalog product once and have it stick
// for every future occurrence") described a capability that was never built,
// which is why the Memory Learnings panel beside it reads zero promoted
// learnings. Do not restore that sentence without the endpoint.

export interface GapFillerNeedPick {
  canonical_key: string;
  distributor_id: string;
  distributor_name: string;
  component_name: string;
  occurrence_count: number;
  miss_reasons: Record<string, number>;
  first_seen: string;
  last_seen: string;
  drill_down_token: string;
}

export interface GapFillerNeedsPickResponse {
  needs_pick: GapFillerNeedPick[];
  count: number;
}

export async function getGapFillerNeedsPick(params?: {
  distributor_id?: string;
  limit?: number;
}): Promise<ApiResponse<GapFillerNeedsPickResponse>> {
  const searchParams = new URLSearchParams();
  if (params?.distributor_id) searchParams.set('distributor_id', params.distributor_id);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const qs = searchParams.toString();
  return fetchWithAuth(`/api/v1/admin/gap_filler_needs_pick${qs ? `?${qs}` : ''}`);
}

export interface GapFillerSourceQuote {
  quote_id: string;
  quote_status: string;
  restaurant_id: string;
  restaurant_name: string;
  rep_id: string;
  rep_email: string;
  miss_reason: string;
  occurred_at: string;
}

// The drill-down endpoint returns an OBJECT ({ canonical_key, distributor_id,
// quotes: [...], count }), not a bare array — the source quotes live under
// `.quotes`. (This mismatch is exactly why the row expander rendered empty:
// the caller was setting the whole object where a GapFillerSourceQuote[] was
// expected, so `.length` was undefined and neither the empty-state nor the
// table branch fired.)
export interface GapFillerDrillDownResponse {
  canonical_key: string;
  distributor_id: string;
  quotes: GapFillerSourceQuote[];
  count: number;
}

export async function getGapFillerNeedsPickQuotes(
  drillDownToken: string
): Promise<ApiResponse<GapFillerDrillDownResponse>> {
  return fetchWithAuth(`/api/v1/admin/gap_filler_needs_pick/${encodeURIComponent(drillDownToken)}/quotes`);
}

// ─── Operational Memory Epic, Lane 2: QM-admin learnings view ─────────────
//
// Read + revert view over every promoted memory row (rep tier + distributor
// "preferred" tier), full provenance. NEVER shown to distributors -- this
// lives only under the quoteme_admin-gated /api/v1/admin namespace.

export interface OperationalMemoryLearning {
  id: string;
  tier: 'rep' | 'preferred';
  /** Only meaningful for tier "preferred" -- null for rep-tier rows.
   * Ruling 2: disambiguates a distributor-tier row into a PREFERENCE
   * (presentation only) or a MANDATE (must be visible + attributable). */
  distributor_signal_type?: 'preference' | 'mandate' | null;
  /** Present only when distributor_signal_type is "mandate". */
  mandate_reason?: string | null;
  /** Present only when distributor_signal_type is "mandate" -- who set it. */
  mandate_set_by?: { id: string; name: string; email: string } | null;
  canonical_key: string;
  category: string | null;
  catalog_version: string;
  policy_version: string;
  active: boolean;
  reverted_at: string | null;
  product: { id: string; name: string; brand: string | null; item_number: string } | null;
  distributor: { id: string; name: string } | null;
  rep: { id: string; name: string; email: string } | null;
  provenance: {
    promoted_from_operational_event_id: string | null;
    promoted_from_quote_id: string | null;
    promoted_by_user_id: string | null;
    promoted_by: { id: string; name: string; email: string } | null;
    promoted_at: string | null;
    correction_type: string | null;
    quote_id: string | null;
  };
}

export interface OperationalMemoryLearningsResponse {
  learnings: OperationalMemoryLearning[];
  count: number;
}

export async function getOperationalMemoryLearnings(params?: {
  distributor_id?: string;
  rep_id?: string;
  tier?: 'rep' | 'preferred';
  include_reverted?: boolean;
  limit?: number;
}): Promise<ApiResponse<OperationalMemoryLearningsResponse>> {
  const searchParams = new URLSearchParams();
  if (params?.distributor_id) searchParams.set('distributor_id', params.distributor_id);
  if (params?.rep_id) searchParams.set('rep_id', params.rep_id);
  if (params?.tier) searchParams.set('tier', params.tier);
  if (params?.include_reverted) searchParams.set('include_reverted', 'true');
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const qs = searchParams.toString();
  return fetchWithAuth(`/api/v1/admin/operational_memory_learnings${qs ? `?${qs}` : ''}`);
}

// Future-only: stops the row from surfacing on any FUTURE alignment run.
// Never touches operational_events or any already-sent/drafted quote.
export async function revertOperationalMemoryLearning(
  id: string
): Promise<ApiResponse<OperationalMemoryLearning>> {
  return fetchWithAuth(`/api/v1/admin/operational_memory_learnings/${id}/revert`, { method: 'POST' });
}
